import { coerceNumberProperty } from '@angular/cdk/coercion';
import { AsyncPipe, NgIf, NgOptimizedImage, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { CloudinaryModule, ImageCropMode } from '@x/common/cloudinary';
import { LoaderModule } from '@x/common/loader';
import { loaderSize } from '@x/common/loader/loader.component';
import {
  IMediaSource,
  TMediaResponsiveConfig,
} from '@x/common/media-thumbnail/media-thumbnail.config';
import { DeviceSizeMediaQueries, DeviceSizes } from '@x/common/responsive';
import {
  MediaType,
  getMediaTypeFromUrl,
  getVimeoVideoThumbnailFromUrl,
  getYoutubeVideoThumbnailFromUrl,
} from '@x/common/utils/media';
import { BehaviorSubject } from 'rxjs';

@Component({
  standalone: true,
  selector: 'x-media-thumbnail',
  templateUrl: './media-thumbnail.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'x-media-thumbnail' },
  preserveWhitespaces: false,
  imports: [NgOptimizedImage, CloudinaryModule, LoaderModule, NgIf, AsyncPipe, NgTemplateOutlet],
})
export class MediaThumbnailComponent {
  @Input()
  set type(type: string | MediaType) {
    this._type = type;
    this.updateThumbnail();
  }
  get type() {
    return this._type;
  }

  @Input()
  set url(url: string | undefined) {
    this._url = url;
    this.updateThumbnail();
    if (!this._type && url) {
      this._type = getMediaTypeFromUrl(url) ?? MediaType.IMAGE;
    }
  }
  get url() {
    return this._url;
  }

  @Input()
  set width(width: number | undefined | string) {
    this._width = coerceNumberProperty(width);
  }
  get width() {
    return this._width;
  }

  @Input()
  set height(height: number | undefined | string) {
    this._height = coerceNumberProperty(height);
  }
  get height() {
    return this._height;
  }

  @Input()
  crop: ImageCropMode = 'thumb';

  @Output() mediaLoaded: EventEmitter<boolean> = new EventEmitter();

  @Input()
  loader: boolean = true;

  @Input()
  loaderSize: loaderSize = 'default';

  @Input() imageLoading: 'eager' | 'lazy' = 'lazy';

  loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  @Input() set responsiveConfig(config: TMediaResponsiveConfig | undefined) {
    if (!config) return;

    this._imageSources = Object.entries(config).map(([deviceSize, { width, height }]) => ({
      mediaQuery: DeviceSizeMediaQueries[deviceSize as DeviceSizes],
      width,
      height,
    }));
  }

  get imageSources() {
    return this._imageSources;
  }

  _width: number | undefined;
  _height: number | undefined;
  _url: string | undefined;
  _type: string | MediaType = MediaType.IMAGE;
  _thumbnailUrl: string;
  private _imageSources: IMediaSource[] = [];

  updateThumbnail() {
    if (this._url) {
      switch (this.type) {
        case MediaType.YOUTUBE:
          this._thumbnailUrl = getYoutubeVideoThumbnailFromUrl(this._url);
          break;
        case MediaType.VIMEO:
          this._thumbnailUrl = getVimeoVideoThumbnailFromUrl(this._url);
          break;
        default:
          this._thumbnailUrl = this._url;
      }
    }
  }

  loaded() {
    this.loading$.next(false);
    this.mediaLoaded.next(true);
  }
}
